import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Ship, Plane, Zap, Package, Check, Search, MapPin, Phone, MessageCircle } from "lucide-react";
import { useT } from "@/context/LangContext";

const HERO_IMG = "https://images.unsplash.com/photo-1494412651409-8963ce7935a7?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

export default function Freight() {
  const t = useT();
  const nav = useNavigate();
  const [tn, setTn] = useState("");

  const submitTrack = (e) => {
    e.preventDefault();
    if (tn.trim()) nav(`/suivi?n=${encodeURIComponent(tn.trim().toUpperCase())}`);
  };

  return (
    <div data-testid="freight-page">
      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl w-full mx-auto px-6 lg:px-10 pb-20 pt-28 text-white">
          <p className="kicker !text-[#FF6B00]">{t("freight.kicker")}</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter mt-6 max-w-4xl">
            {t("freight.title")}
          </h1>
          <p className="text-white/70 mt-6 max-w-2xl text-base sm:text-lg leading-relaxed">
            {t("freight.desc")}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact" className="btn-primary" data-testid="freight-cta-quote">
              {t("freight.cta.quote")} <ArrowRight size={16} />
            </Link>
            <Link to="/suivi" className="btn-outline !text-white !border-white/40 hover:!bg-white hover:!text-[var(--ink)]" data-testid="freight-cta-track">
              <Search size={16} /> {t("freight.cta.track")}
            </Link>
          </div>
        </div>
      </section>

      {/* Quick tracking widget */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 -mt-10 relative z-10">
        <form onSubmit={submitTrack} className="card-flat p-6 lg:p-8 shadow-2xl" data-testid="freight-inline-track">
          <p className="kicker">{t("freight.track.title")}</p>
          <div className="flex flex-col md:flex-row gap-3 mt-4">
            <input
              value={tn}
              onChange={(e) => setTn(e.target.value)}
              placeholder={t("freight.track.placeholder")}
              className="input-field flex-1 font-mono uppercase"
              data-testid="freight-tn-input"
            />
            <button type="submit" className="btn-primary justify-center" data-testid="freight-tn-submit">
              <Search size={16} /> {t("freight.track.btn")}
            </button>
          </div>
        </form>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <h2 className="text-4xl lg:text-5xl font-display font-black tracking-tighter max-w-3xl">
          {t("freight.services.title")}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {[
            { k: "express", icon: Zap, featured: true },
            { k: "normal", icon: Plane, featured: false },
            { k: "maritime", icon: Ship, featured: false },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.k}
                className={`card-flat p-8 relative overflow-hidden flex flex-col ${s.featured ? "border-2 border-[var(--accent)]" : ""}`}
                data-testid={`freight-${s.k}`}
              >
                <span className={`absolute top-6 right-6 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${s.featured ? "bg-[var(--accent)] text-white" : "bg-[var(--secondary)]/10 text-[var(--secondary)]"}`}>
                  {t(`freight.${s.k}.badge`)}
                </span>
                <Icon className={s.featured ? "text-[var(--accent)]" : "text-[var(--secondary)]"} size={30} />
                <h3 className="font-display text-xl font-bold mt-6">{t(`freight.${s.k}.title`)}</h3>
                <p className="text-xs uppercase tracking-widest text-[var(--ink-soft)] mt-3">{t(`freight.${s.k}.time`)}</p>
                <p className="font-display text-3xl font-black text-[var(--accent)] mt-2 leading-tight">{t(`freight.${s.k}.price`)}</p>
                {s.k === "maritime" && (
                  <p className="text-sm font-bold text-[var(--accent)] mt-1">
                    🧱 Carreaux : 200 000 FCFA/m³
                  </p>
                )}
                <p className="text-xs text-[var(--ink-soft)]">{t(`freight.${s.k}.price.hint`)}</p>
                <p className="text-[var(--ink-soft)] mt-5 text-sm">{t(`freight.${s.k}.desc`)}</p>
                <ul className="mt-5 space-y-2 text-sm flex-1">
                  {["f1", "f2", "f3"].map((f) => (
                    <li key={f} className="flex gap-2 items-start">
                      <Check size={14} className="text-[var(--accent)] mt-0.5 shrink-0" />
                      <span>{t(`freight.${s.k}.${f}`)}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className={`mt-6 ${s.featured ? "btn-primary" : "btn-outline"} justify-center`} data-testid={`freight-${s.k}-cta`}>
                  {t("freight.cta.quote")} <ArrowRight size={14} />
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* Dakar pickup point */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8">
        <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-[#0A0F1A] to-[#1a2540] text-white relative">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[var(--accent)]/10 blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-0">
            <div className="lg:col-span-7 p-8 lg:p-12">
              <p className="kicker !text-[#FF6B00] flex items-center gap-2">
                <MapPin size={14} /> {t("freight.pickup.kicker")}
              </p>
              <h2 className="font-display text-3xl lg:text-4xl font-black mt-4 leading-tight">
                {t("freight.pickup.title")}
              </h2>
              <p className="text-white/70 mt-4 max-w-xl">{t("freight.pickup.desc")}</p>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border-l-2 border-[var(--accent)] pl-4">
                  <p className="text-xs uppercase tracking-widest text-white/50">{t("freight.pickup.addr.label")}</p>
                  <p className="mt-1 font-bold leading-snug">{t("freight.pickup.addr")}</p>
                </div>
                <div className="border-l-2 border-[var(--accent)] pl-4">
                  <p className="text-xs uppercase tracking-widest text-white/50">{t("freight.pickup.agent.label")}</p>
                  <p className="mt-1 font-bold">+221 77 441 90 60</p>
                  <p className="text-xs text-white/50 mt-1">{t("freight.pickup.agent.hint")}</p>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:+221774419060"
                  className="flex-1 flex items-center gap-3 px-5 py-4 rounded-lg bg-[var(--accent)] hover:bg-[var(--accent-hover)] transition-colors group"
                  data-testid="freight-pickup-call"
                >
                  <Phone size={18} />
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-widest text-white/70">{t("freight.pickup.call")}</p>
                    <p className="font-bold">+221 77 441 90 60</p>
                  </div>
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href="https://wa.me/221774419060"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center gap-3 px-5 py-4 rounded-lg bg-[#25D366] hover:bg-[#1fb954] transition-colors group"
                  data-testid="freight-pickup-whatsapp"
                >
                  <MessageCircle size={18} />
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-widest text-white/70">{t("freight.pickup.whatsapp")}</p>
                    <p className="font-bold">+221 77 441 90 60</p>
                  </div>
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 relative min-h-[380px] bg-black/40" data-testid="freight-pickup-map">
              <iframe
                title="Point de retrait Dakar Mariste"
                src="https://www.google.com/maps?q=Dakar+Mariste+BICIS+SGBS&hl=fr&z=15&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(0.15) contrast(1.05)" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="absolute inset-0 w-full h-full"
              />
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=Dakar+Mariste+BICIS+SGBS+Senegal"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-4 left-4 right-4 inline-flex items-center justify-center gap-2 px-4 py-3 bg-white text-[var(--ink)] rounded-lg font-bold shadow-2xl hover:bg-[var(--bg-soft)] transition-colors"
                data-testid="freight-pickup-directions"
              >
                <MapPin size={16} className="text-[var(--accent)]" />
                {t("freight.pickup.directions")}
                <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="bg-[var(--bg-soft)] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="kicker">{t("freight.included")}</p>
            <h2 className="text-3xl lg:text-4xl font-display font-black mt-4 leading-tight">
              Yiwu <span className="text-[var(--accent)]">→</span> Dakar, sans stress.
            </h2>
          </div>
          <ul className="lg:col-span-7 space-y-4">
            {["i1", "i2", "i3", "i4", "i5"].map((k, i) => (
              <li key={k} className="card-flat p-5 flex gap-4 items-start" data-testid={`freight-${k}`}>
                <span className="w-8 h-8 rounded-full bg-[var(--accent)]/10 text-[var(--accent)] font-bold flex items-center justify-center shrink-0">
                  {i + 1}
                </span>
                <p className="text-[var(--ink)] mt-1">{t(`freight.${k}`)}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 text-center">
        <Package className="mx-auto text-[var(--accent)]" size={40} />
        <h2 className="font-display text-3xl lg:text-5xl font-black mt-6 max-w-2xl mx-auto">
          {t("freight.cta.quote")} ?
        </h2>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to="/contact" className="btn-primary">{t("home.cta.contact")} <ArrowRight size={16} /></Link>
          <Link to="/suivi" className="btn-outline"><Search size={16} /> {t("freight.cta.track")}</Link>
        </div>
      </section>
    </div>
  );
}
